import {
  Archive,
  Download,
  FileText,
  Folder,
  History,
  Link2,
  MoreVertical,
  Plus,
  Trash2,
  X,
  Columns2,
  PanelRight,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useMemo } from "react";
import { useTabs, type TabGroup } from "@/store/tabs";
import { useAllSpaceItems } from "@/data/store";
import { useSharedFolders, sharedAccent } from "@/data/sharedFolders";
import { useUi } from "@/store/ui";
import { createNote, archiveItem, trashItem } from "@/commands/nav";
import { exportNote, exportZip } from "@/data/export";
import { useHistoryDialog } from "@/components/HistoryDialog";
import { useShareDialog } from "@/components/ShareDialog";
import { IconButton } from "@/ui/IconButton";
import s from "./TabBar.module.css";

export function TabBar({ group, isLastGroup }: { group: TabGroup; isLastGroup: boolean }) {
  const spaces = useAllSpaceItems();
  const folders = useSharedFolders((st) => st.folders);
  const items = useMemo(() => spaces.flatMap((sp) => sp.items), [spaces]);
  // itemId -> accent color for shared items (tab color dot)
  const accentOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const sp of spaces) {
      if (sp.kind !== "shared") continue;
      const meta = folders.find((f) => f.id === sp.spaceId);
      if (!meta) continue;
      const accent = sharedAccent(meta.colorKey);
      for (const i of sp.items) map.set(i.id, accent);
    }
    return map;
  }, [spaces, folders]);
  const tabs = useTabs();
  const { rightCollapsed, toggleRight } = useUi();
  const activeItem = items.find((i) => i.id === group.tabs.find((t) => t.id === group.activeTabId)?.itemId);
  const activeShared = activeItem ? accentOf.has(activeItem.id) : false;

  return (
    <div className={s.bar} role="tablist">
      {group.tabs.map((tab) => {
        const item = items.find((i) => i.id === tab.itemId);
        const active = tab.id === group.activeTabId;
        return (
          <div
            key={tab.id}
            role="tab"
            aria-selected={active}
            className={[s.tab, active ? s.tabActive : ""].join(" ")}
            onClick={() => tabs.activateTab(group.id, tab.id)}
            onAuxClick={(e) => {
              if (e.button === 1) tabs.closeTab(group.id, tab.id);
            }}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData("application/x-hydra-tab", JSON.stringify({ groupId: group.id, tabId: tab.id }));
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const raw = e.dataTransfer.getData("application/x-hydra-tab");
              if (!raw) return;
              const { groupId, tabId } = JSON.parse(raw) as { groupId: string; tabId: string };
              const idx = group.tabs.findIndex((t) => t.id === tab.id);
              tabs.moveTab(groupId, tabId, group.id, idx);
            }}
          >
            <span
              className={s.tabIcon}
              style={accentOf.has(tab.itemId) ? { color: accentOf.get(tab.itemId) } : undefined}
            >
              {item?.type === "folder" ? (
                <Folder size={12} />
              ) : (
                <FileText size={12} />
              )}
            </span>
            <span className={s.tabLabel}>
              {item?.title || "Untitled"}
            </span>
            <span
              className={s.tabClose}
              role="button"
              aria-label="Close tab"
              onClick={(e) => {
                e.stopPropagation();
                tabs.closeTab(group.id, tab.id);
              }}
            >
              <X size={11} />
            </span>
          </div>
        );
      })}
      <div className={s.actions}>
        <IconButton label="New note" kbd="Mod+N" onClick={() => createNote(null)}>
          <Plus size={14} />
        </IconButton>
        {group.activeTabId ? (
          <IconButton label="Split right" onClick={() => tabs.splitRight(group.id, group.activeTabId!)}>
            <Columns2 size={14} />
          </IconButton>
        ) : null}
        {activeItem ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <span>
                <IconButton label="Note actions">
                  <MoreVertical size={14} />
                </IconButton>
              </span>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="hn-menu" align="end" sideOffset={6}>
                {activeItem.type === "note" ? (
                  <>
                    <DropdownMenu.Item
                      className="hn-menu-item"
                      onSelect={() => useShareDialog.getState().open(activeItem.id)}
                    >
                      <Link2 /> Share as public link
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="hn-menu-item"
                      onSelect={() => useHistoryDialog.getState().open(activeItem.id)}
                    >
                      <History /> Version history
                    </DropdownMenu.Item>
                  </>
                ) : null}
                {activeItem.type !== "tasklist" ? (
                  <DropdownMenu.Item
                    className="hn-menu-item"
                    onSelect={() =>
                      activeItem.type === "note" ? void exportNote(activeItem.id) : void exportZip(activeItem.id)
                    }
                  >
                    <Download /> Export {activeItem.type === "note" ? "markdown" : "as zip"}
                  </DropdownMenu.Item>
                ) : null}
                <DropdownMenu.Separator className="hn-menu-sep" />
                {!activeShared && activeItem.builtin !== "tasks" ? (
                  <DropdownMenu.Item className="hn-menu-item" onSelect={() => archiveItem(activeItem.id)}>
                    <Archive /> Archive
                  </DropdownMenu.Item>
                ) : null}
                {activeItem.builtin !== "tasks" ? (
                  <DropdownMenu.Item className="hn-menu-item danger" onSelect={() => trashItem(activeItem.id)}>
                    <Trash2 /> Move to trash
                  </DropdownMenu.Item>
                ) : null}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : null}
        {isLastGroup ? (
          <IconButton label={rightCollapsed ? "Show details panel" : "Hide details panel"} active={!rightCollapsed} onClick={toggleRight}>
            <PanelRight size={14} />
          </IconButton>
        ) : null}
      </div>
    </div>
  );
}
